import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Menu, X, Home, UtensilsCrossed, Calendar,
  Settings, LogOut, Globe, Moon, Sun, Check,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { Logo } from './Logo';
import { useAuthStore } from '../store/authStore';
import { useTheme } from '../hooks/useTheme';
import { cn } from '@/lib/utils';

interface LayoutProps {
  children: React.ReactNode;
}

const LANGUAGES = [
  { code: 'es', label: 'Español' },
  { code: 'en', label: 'English' },
];

function countryLabel(code: string, t: (k: string) => string) {
  if (code === 'ARG') return t('common.argentina');
  if (code === 'CHL') return t('common.chile');
  return t('common.colombia');
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { t } = useTranslation();
  const { isDark, toggle: toggleTheme } = useTheme();

  // Cerrar dropdown al hacer click afuera
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleLangChange = (code: string) => {
    i18n.changeLanguage(code);
    localStorage.setItem('almorzapp-language', code);
    setLangOpen(false);
  };

  const navigationItems = [
    { name: t('nav.home'),    path: '/',         icon: Home            },
    { name: t('nav.myOrder'), path: '/my-order',  icon: UtensilsCrossed },
    { name: t('nav.history'), path: '/history',   icon: Calendar        },
    ...(user?.role === 'ADMIN'
      ? [{ name: t('nav.admin'), path: '/admin', icon: Settings }]
      : []),
  ];

  return (
    <div className="flex h-screen bg-background">
      {/* ── Sidebar ── */}
      <aside
        className={cn(
          'bg-sidebar text-sidebar-foreground flex flex-col transition-all duration-300 ease-in-out',
          sidebarOpen ? 'w-64' : 'w-20',
        )}
      >
        {/* Logo */}
        <div className="p-4 border-b border-white/10">
          {sidebarOpen ? (
            <Logo size="sm" className="text-sidebar-foreground" />
          ) : (
            <div className="flex justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128" fill="none" className="w-8 h-8 text-white" aria-hidden="true">
                <path fill="currentColor" d="M50.4 78.5a75.1 75.1 0 0 0-28.5 6.9l24.2-65.7c.7-2 1.9-3.2 3.4-3.2h29c1.5 0 2.7 1.2 3.4 3.2l24.2 65.7s-11.6-7-28.5-7L67 45.5c-.4-1.7-1.6-2.8-2.9-2.8-1.3 0-2.5 1.1-2.9 2.7L50.4 78.5Zm-1.1 28.2Zm-4.2-20.2c-2 6.6-.6 15.8 4.2 20.2a17.5 17.5 0 0 1 .2-.7 5.5 5.5 0 0 1 5.7-4.5c2.8.1 4.3 1.5 4.7 4.7.2 1.1.2 2.3.2 3.5v.4c0 2.7.7 5.2 2.2 7.4a13 13 0 0 0 5.7 4.9v-.3l-.2-.3c-1.8-5.6-.5-9.5 4.4-12.8l1.5-1a73 73 0 0 0 3.2-2.2 16 16 0 0 0 6.8-11.4c.3-2 .1-4-.6-6l-.8.6-1.6 1a37 37 0 0 1-22.4 2.7c-5-.7-9.7-2-13.2-6.2Z"/>
              </svg>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                title={!sidebarOpen ? item.name : ''}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors duration-150 text-sm font-medium',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-sidebar-foreground/60 hover:bg-white/10 hover:text-sidebar-foreground',
                  !sidebarOpen && 'justify-center',
                )}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {sidebarOpen && <span>{item.name}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User info + logout */}
        <div className="p-4 border-t border-white/10">
          {sidebarOpen && user && (
            <div className="mb-3 pb-3 border-b border-white/10 space-y-0.5">
              <p className="text-sidebar-foreground text-sm font-medium truncate">{user.name}</p>
              <p className="text-sidebar-foreground/50 text-xs truncate">{user.email}</p>
              <p className="text-primary text-xs">
                {user.role === 'ADMIN' ? t('nav.administrator') : t('nav.user')}
              </p>
              {user.role === 'ADMIN' && user.adminCountry ? (
                <>
                  <p className="text-sidebar-foreground/50 text-xs">
                    {t('nav.manages')}: {countryLabel(user.adminCountry, t)}
                  </p>
                  {user.originalCountry && user.originalCountry !== user.adminCountry && (
                    <p className="text-sidebar-foreground/40 text-xs">
                      {t('nav.personal')}: {countryLabel(user.originalCountry, t)}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sidebar-foreground/50 text-xs">
                  {user.country ? countryLabel(user.country, t) : ''}
                </p>
              )}
            </div>
          )}

          <button
            onClick={handleLogout}
            title={!sidebarOpen ? t('nav.signOut') : ''}
            className={cn(
              'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors duration-150 text-sm w-full',
              'text-sidebar-foreground/60 hover:bg-destructive/20 hover:text-destructive',
              !sidebarOpen && 'justify-center',
            )}
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarOpen && <span className="font-medium">{t('nav.signOut')}</span>}
          </button>
        </div>
      </aside>

      {/* ── Main content ── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar — glass effect */}
        <header className="glass px-6 py-3 flex items-center justify-between sticky top-0 z-30">
          {/* Toggle sidebar */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-muted transition-colors duration-150"
          >
            {sidebarOpen
              ? <X    className="w-5 h-5 text-foreground" />
              : <Menu className="w-5 h-5 text-foreground" />}
          </button>

          {/* Right side: icons + date */}
          <div className="flex items-center gap-3">
            {/* Dark mode toggle */}
            <button
              onClick={toggleTheme}
              title={isDark ? 'Modo claro' : 'Modo oscuro'}
              className="p-2 rounded-lg hover:bg-muted transition-colors duration-150 text-muted-foreground hover:text-foreground"
            >
              {isDark
                ? <Sun  className="w-4 h-4" />
                : <Moon className="w-4 h-4" />}
            </button>

            {/* Language switcher */}
            <div ref={langRef} className="relative">
              <button
                onClick={() => setLangOpen((v) => !v)}
                title="Language / Idioma"
                className="p-2 rounded-lg hover:bg-muted transition-colors duration-150 text-muted-foreground hover:text-foreground"
              >
                <Globe className="w-4 h-4" />
              </button>

              {langOpen && (
                <div className="absolute right-0 top-full mt-1 w-36 bg-card border border-border rounded-lg shadow-md overflow-hidden animate-scale-in z-50">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLangChange(lang.code)}
                      className="flex items-center justify-between w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted transition-colors duration-150"
                    >
                      <span>{lang.label}</span>
                      {i18n.language === lang.code && (
                        <Check className="w-3.5 h-3.5 text-primary" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Date / time */}
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-foreground leading-none">
                {new Date().toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'es-AR', {
                  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                })}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {new Date().toLocaleTimeString(i18n.language === 'en' ? 'en-US' : 'es-AR', {
                  hour: '2-digit', minute: '2-digit',
                })}
              </p>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6 animate-fade-in">{children}</main>
      </div>
    </div>
  );
};
